import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Password } from 'primereact/password';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import axios from 'axios';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showTeacherDialog, setShowTeacherDialog] = useState(false);
    const [showNonAcademicDialog, setShowNonAcademicDialog] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [teachersWithoutLogin, setTeachersWithoutLogin] = useState([]);
    
    const [teacherFormData, setTeacherFormData] = useState({
        username: '',
        password: '',
        roleId: 4,
        profileId: null,
        selectedTeacher: null
    });
    
    const [nonAcademicFormData, setNonAcademicFormData] = useState({
        username: '',
        password: '',
        roleId: null,
        firstName: '',
        lastName: '',
        nic: '',
        phone: '',
        whatsapp: ''
    });
    
    const [newPassword, setNewPassword] = useState('');
    const toast = React.useRef(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            showToast('error', 'Error', 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoles(response.data.data || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    }, []);

    const fetchTeachersWithoutLogin = useCallback(async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers-without-login`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeachersWithoutLogin(response.data.data || []);
        } catch (error) {
            console.error('Error fetching teachers:', error);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchTeachersWithoutLogin();
    }, [fetchUsers, fetchRoles, fetchTeachersWithoutLogin]);

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    const openTeacherDialog = () => {
        setTeacherFormData({
            username: '',
            password: '',
            roleId: 4,
            profileId: null,
            selectedTeacher: null
        });
        fetchTeachersWithoutLogin();
        setShowTeacherDialog(true);
    };

    const openNonAcademicDialog = () => {
        setNonAcademicFormData({
            username: '',
            password: '',
            roleId: null,
            firstName: '',
            lastName: '',
            nic: '',
            phone: '',
            whatsapp: ''
        });
        setShowNonAcademicDialog(true);
    };

    const handleTeacherSelect = (teacher) => {
        setTeacherFormData({
            ...teacherFormData,
            selectedTeacher: teacher,
            profileId: teacher.profileId,
            username: teacher.nic || `T${teacher.teacherDisplayId}`
        });
    };

    const handleSaveTeacherUser = async () => {
        if (!teacherFormData.profileId || !teacherFormData.username.trim() || !teacherFormData.password.trim()) {
            showToast('warn', 'Validation', 'Please select a teacher and enter username/password');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/users`, {
                userType: 'teacher',
                profileId: teacherFormData.profileId,
                username: teacherFormData.username,
                password: teacherFormData.password,
                roleId: teacherFormData.roleId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showToast('success', 'Success', 'Teacher user created successfully');
            setShowTeacherDialog(false);
            fetchUsers();
            fetchTeachersWithoutLogin();
        } catch (error) {
            console.error('Error creating teacher user:', error);
            showToast('error', 'Error', error.response?.data?.message || 'Failed to create user');
        }
    };

    const handleSaveNonAcademicUser = async () => {
        const { username, password, roleId, firstName, lastName, nic, phone } = nonAcademicFormData;
        if (!username.trim() || !password.trim() || !roleId || !firstName.trim() || !lastName.trim()) {
            showToast('warn', 'Validation', 'Please fill all required fields');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/users`, {
                userType: 'non-academic',
                username,
                password,
                roleId,
                firstName,
                lastName,
                nic,
                phone,
                whatsapp: nonAcademicFormData.whatsapp
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showToast('success', 'Success', 'Non-academic user created successfully');
            setShowNonAcademicDialog(false);
            fetchUsers();
        } catch (error) {
            console.error('Error creating non-academic user:', error);
            showToast('error', 'Error', error.response?.data?.message || 'Failed to create user');
        }
    };

    const handleToggleStatus = async (user) => {
        const newStatus = user.isActive ? 0 : 1;
        const action = newStatus ? 'activate' : 'deactivate';
        
        confirmDialog({
            message: `Are you sure you want to ${action} user "${user.username}"?`,
            header: 'Confirm Action',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: newStatus ? 'p-button-success' : 'p-button-danger',
            accept: async () => {
                try {
                    const token = localStorage.getItem('adminToken');
                    await axios.put(`${API_BASE_URL}/admin/users/${user.id}/status`, 
                        { isActive: newStatus },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    showToast('success', 'Success', `User ${action}d successfully`);
                    fetchUsers();
                } catch (error) {
                    console.error('Error updating status:', error);
                    showToast('error', 'Error', 'Failed to update user status');
                }
            }
        });
    };

    const openResetDialog = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowResetDialog(true);
    };

    const handleResetPassword = async () => {
        if (!newPassword.trim()) {
            showToast('warn', 'Validation', 'Please enter a new password');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/users/${selectedUser.id}/reset-password`, 
                { password: newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showToast('success', 'Success', 'Password reset successfully');
            setShowResetDialog(false);
        } catch (error) {
            console.error('Error resetting password:', error);
            showToast('error', 'Error', 'Failed to reset password');
        }
    };

    const handleDelete = (user) => {
        confirmDialog({
            message: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const token = localStorage.getItem('adminToken');
                    await axios.delete(`${API_BASE_URL}/admin/users/${user.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showToast('success', 'Success', 'User deleted successfully');
                    fetchUsers();
                } catch (error) {
                    console.error('Error deleting user:', error);
                    showToast('error', 'Error', error.response?.data?.message || 'Failed to delete user');
                }
            }
        });
    };

    const statusTemplate = (rowData) => {
        return (
            <span className={`status-badge ${rowData.isActive ? 'status-active' : 'status-inactive'}`}>
                <i className={`pi ${rowData.isActive ? 'pi-check-circle' : 'pi-times-circle'}`}></i>
                {rowData.isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    const roleTemplate = (rowData) => {
        return (
            <span style={{ 
                padding: '4px 10px', 
                borderRadius: '12px', 
                background: '#e3f2fd', 
                color: '#1565c0',
                fontSize: '0.85rem',
                fontWeight: '500'
            }}>
                {rowData.roleName}
            </span>
        );
    };

    const userTypeTemplate = (rowData) => {
        const isTeacher = rowData.userType === 'teacher';
        return (
            <span style={{ 
                padding: '4px 10px', 
                borderRadius: '12px', 
                background: isTeacher ? '#e8f5e9' : '#fff3e0', 
                color: isTeacher ? '#2e7d32' : '#e65100',
                fontSize: '0.85rem',
                fontWeight: '500'
            }}>
                <i className={`pi ${isTeacher ? 'pi-users' : 'pi-briefcase'}`} style={{ marginRight: '5px' }}></i>
                {isTeacher ? 'Teacher' : 'Non-Academic'}
            </span>
        );
    };

    const actionsTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                <Button
                    icon="pi pi-key"
                    className="p-button-rounded p-button-info p-button-sm"
                    tooltip="Reset Password"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => openResetDialog(rowData)}
                />
                <Button
                    icon={rowData.isActive ? 'pi pi-ban' : 'pi pi-check'}
                    className={`p-button-rounded p-button-sm ${rowData.isActive ? 'p-button-warning' : 'p-button-success'}`}
                    tooltip={rowData.isActive ? 'Deactivate' : 'Activate'}
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleToggleStatus(rowData)}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-sm"
                    tooltip="Delete"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleDelete(rowData)}
                />
            </div>
        );
    };

    const header = (
        <div className="admin-section-header">
            <h2><i className="pi pi-user-plus"></i> System Users</h2>
            <div className="admin-toolbar">
                <div className="admin-search-box">
                    <i className="pi pi-search"></i>
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search users..."
                    />
                </div>
                <Button
                    label="Add Teacher User"
                    icon="pi pi-users"
                    className="p-button-success"
                    onClick={openTeacherDialog}
                />
                <Button
                    label="Add Non-Academic"
                    icon="pi pi-briefcase"
                    className="p-button-info"
                    onClick={openNonAcademicDialog}
                />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    tooltip="Refresh"
                    onClick={fetchUsers}
                />
            </div>
        </div>
    );

    const teacherDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowTeacherDialog(false)} />
            <Button label="Create User" icon="pi pi-check" onClick={handleSaveTeacherUser} />
        </div>
    );

    const nonAcademicDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowNonAcademicDialog(false)} />
            <Button label="Create User" icon="pi pi-check" onClick={handleSaveNonAcademicUser} />
        </div>
    );

    const resetDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowResetDialog(false)} />
            <Button label="Reset" icon="pi pi-refresh" className="p-button-warning" onClick={handleResetPassword} />
        </div>
    );

    return (
        <div className="admin-section">
            <Toast ref={toast} />
            <ConfirmDialog />
            
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            <DataTable
                value={users}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                globalFilter={globalFilter}
                header={header}
                emptyMessage="No users found"
                className="admin-table"
                stripedRows
                showGridlines={false}
            >
                <Column field="teacherDisplayId" header="Teacher Sign ID" sortable style={{ width: '140px' }} />
                <Column field="teacherPk" header="Teacher PK" sortable style={{ width: '100px' }} />
                <Column field="id" header="ID" sortable style={{ width: '70px' }} />
                <Column field="username" header="Username" sortable />
                <Column field="fullName" header="Full Name" sortable />
                <Column field="nic" header="NIC" sortable style={{ width: '130px' }} />
                <Column header="Type" body={userTypeTemplate} style={{ width: '140px' }} />
                <Column header="Role" body={roleTemplate} sortable field="roleName" style={{ width: '130px' }} />
                <Column header="Status" body={statusTemplate} style={{ width: '110px' }} />
                <Column header="Actions" body={actionsTemplate} style={{ width: '150px' }} />
            </DataTable>

            {/* Add Teacher as User Dialog */}
            <Dialog
                visible={showTeacherDialog}
                onHide={() => setShowTeacherDialog(false)}
                header="Add Teacher as System User"
                footer={teacherDialogFooter}
                style={{ width: '650px' }}
                className="admin-dialog"
                modal
            >
                <div className="form-grid">
                    <div className="form-field full-width">
                        <label>Select Teacher *</label>
                        <Dropdown
                            value={teacherFormData.selectedTeacher}
                            options={teachersWithoutLogin}
                            onChange={(e) => handleTeacherSelect(e.value)}
                            placeholder="Select a teacher without login"
                            optionLabel={(t) => `${t.teacherDisplayId} - ${t.firstName} ${t.lastName} (${t.nic || 'No NIC'})`}
                            filter
                            filterPlaceholder="Search by name or NIC..."
                            emptyMessage="No teachers available"
                            style={{ width: '100%' }}
                        />
                    </div>
                    {teacherFormData.selectedTeacher && (
                        <div className="form-field full-width" style={{ background: '#f5f5f5', padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                            <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>Selected Teacher:</p>
                            <p style={{ margin: '0' }}>
                                <strong>ID:</strong> {teacherFormData.selectedTeacher.teacherDisplayId} | 
                                <strong> Name:</strong> {teacherFormData.selectedTeacher.firstName} {teacherFormData.selectedTeacher.lastName} | 
                                <strong> NIC:</strong> {teacherFormData.selectedTeacher.nic || 'N/A'}
                            </p>
                        </div>
                    )}
                    <div className="form-field full-width">
                        <label>Username *</label>
                        <InputText
                            value={teacherFormData.username}
                            onChange={(e) => setTeacherFormData({ ...teacherFormData, username: e.target.value })}
                            placeholder="Enter username (NIC recommended)"
                        />
                    </div>
                    <div className="form-field full-width">
                        <label>Password *</label>
                        <Password
                            value={teacherFormData.password}
                            onChange={(e) => setTeacherFormData({ ...teacherFormData, password: e.target.value })}
                            placeholder="Enter password"
                            toggleMask
                            feedback={false}
                        />
                    </div>
                    <div className="form-field full-width">
                        <label>Role *</label>
                        <Dropdown
                            value={teacherFormData.roleId}
                            options={roles}
                            onChange={(e) => setTeacherFormData({ ...teacherFormData, roleId: e.value })}
                            placeholder="Select role"
                            optionLabel="roleName"
                            optionValue="id"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Add Non-Academic User Dialog */}
            <Dialog
                visible={showNonAcademicDialog}
                onHide={() => setShowNonAcademicDialog(false)}
                header="Add Non-Academic Staff User"
                footer={nonAcademicDialogFooter}
                style={{ width: '550px' }}
                className="admin-dialog"
                modal
            >
                <div className="form-grid">
                    <div className="form-field">
                        <label>First Name *</label>
                        <InputText
                            value={nonAcademicFormData.firstName}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, firstName: e.target.value })}
                            placeholder="First name"
                        />
                    </div>
                    <div className="form-field">
                        <label>Last Name *</label>
                        <InputText
                            value={nonAcademicFormData.lastName}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, lastName: e.target.value })}
                            placeholder="Last name"
                        />
                    </div>
                    <div className="form-field">
                        <label>NIC</label>
                        <InputText
                            value={nonAcademicFormData.nic}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, nic: e.target.value })}
                            placeholder="NIC number"
                        />
                    </div>
                    <div className="form-field">
                        <label>Phone</label>
                        <InputText
                            value={nonAcademicFormData.phone}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, phone: e.target.value })}
                            placeholder="Phone number"
                        />
                    </div>
                    <div className="form-field full-width">
                        <label>WhatsApp</label>
                        <InputText
                            value={nonAcademicFormData.whatsapp}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, whatsapp: e.target.value })}
                            placeholder="WhatsApp number"
                        />
                    </div>
                    <div className="form-field full-width" style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '10px' }}>
                        <label>Username *</label>
                        <InputText
                            value={nonAcademicFormData.username}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, username: e.target.value })}
                            placeholder="Enter username"
                        />
                    </div>
                    <div className="form-field full-width">
                        <label>Password *</label>
                        <Password
                            value={nonAcademicFormData.password}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, password: e.target.value })}
                            placeholder="Enter password"
                            toggleMask
                            feedback={false}
                        />
                    </div>
                    <div className="form-field full-width">
                        <label>Role *</label>
                        <Dropdown
                            value={nonAcademicFormData.roleId}
                            options={roles}
                            onChange={(e) => setNonAcademicFormData({ ...nonAcademicFormData, roleId: e.value })}
                            placeholder="Select role"
                            optionLabel="roleName"
                            optionValue="id"
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={showResetDialog}
                onHide={() => setShowResetDialog(false)}
                header={`Reset Password for ${selectedUser?.username}`}
                footer={resetDialogFooter}
                style={{ width: '400px' }}
                className="admin-dialog"
                modal
            >
                <div className="form-field">
                    <label>New Password *</label>
                    <Password
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        toggleMask
                        feedback={false}
                        style={{ width: '100%' }}
                    />
                </div>
            </Dialog>
        </div>
    );
}

export default UserManagement;
